import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { UpdateAvailabilityRestrictionResponse, UpdateAvailabilityRestrictionSchema } from '../../../../services/availability-and-rates/schema/UpdateAvailabilityRestrictionSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateAvailabilityRestriction } from '../../../../services/availability-and-rates/hooks/useUpdateAvailabilityRestriction';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Select from 'react-select';
import CurrencyInput from '../../../../shared/components/form/CurrencyInput';
import clsx from 'clsx';
import FormError from '../../../../shared/components/errors/FormError';
import FormSpinner from '../../../../shared/components/spinner/FormSpinner';
import { SelectedCell } from '../MonthlySetupTable'; // Pastikan path import benar

// Props yang diterima oleh komponen
interface FormRateAvailabilityProps {
    selection: SelectedCell[];
    rateplan: any; // Tipe rateplan dari useShowRatePlan
    onClose: () => void;
    onSuccess: () => void;
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
    onTypeChange: (type: string) => void;
}

const FormRateAvailability: React.FC<FormRateAvailabilityProps> = ({ selection, rateplan, onClose, onSuccess, isFetching, isError, isSuccess, onTypeChange }) => {
    const rateOptions = [
        { value: 'rate', label: 'Rate' },
        { value: 'closed_to_arrival', label: 'Closed to Arrival' },
        { value: 'closed_to_departure', label: 'Closed to Departure' },
        { value: 'stop_sell', label: 'Stop Sell' },
        { value: 'max_stay', label: 'Max Stay' },
        { value: 'min_stay_arrival', label: 'Min Stay Arrival' },
        { value: 'min_stay_through', label: 'Min Stay Through' },
    ];

    // Ambil info dari sel pertama (karena sudah divalidasi bahwa semua sama)
    const firstCell = selection[0];
    const { rateId, restrictionType } = firstCell;

    const { handleSubmit, formState: { errors }, control, setError, reset, watch } = useForm<UpdateAvailabilityRestrictionResponse>({
        resolver: zodResolver(UpdateAvailabilityRestrictionSchema),
    });

    // Mengatur nilai awal form ketika 'selection' berubah
    useEffect(() => {
        if (selection.length > 0) {
            // Kita hanya perlu mengatur satu item di form 'value' untuk menampung input pengguna.
            // Payload sebenarnya akan dibangun saat submit.
            reset({
                value: [{
                    type: restrictionType,
                    rate: undefined, // Biarkan kosong agar diisi pengguna
                    rate_plan_id: rateId,
                    date_from: '', // Tidak relevan untuk UI, tapi perlu untuk skema
                    date_to: '',   // Tidak relevan untuk UI, tapi perlu untuk skema
                }]
            });
        }
    }, [selection, restrictionType, rateId, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const [errorMultiple, setErrorMultiple] = useState<any>(null);
    const { mutate, isPending } = useUpdateAvailabilityRestriction();
    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateAvailabilityRestrictionResponse) => {
        toast.loading('Saving Restrictions...', { id: 'save-restrictions' });

        // Ambil nilai rate tunggal yang dimasukkan pengguna
        const rateToApply = data.value[0].rate;
        if (rateToApply === undefined || rateToApply === null) {
            toast.error('Please enter a rate value.', { id: 'save-restrictions' });
            setError('value.0.rate', { type: 'manual', message: 'Rate is required' });
            return;
        }

        // Bangun payload untuk API dengan memetakan setiap sel yang dipilih
        const payloadValues = selection.map(cell => ({
            date_from: cell.date,
            date_to: cell.date,
            type: restrictionType,
            rate_plan_id: rateId,
            rate: rateToApply, // Terapkan rate yang sama untuk semua
        }));

        mutate({ values: payloadValues }, {
            onSuccess: async () => {
                // Refetch query yang relevan untuk memperbarui UI.
                // 'price-adjustment' WAJIB ikut: baris DPM di grid dihitung backend
                // dari rate yang baru saja diubah, dan queryKey-nya
                // (['price-adjustment', filter]) tidak mengandung 'monthly'/'occupancy'
                // sehingga tidak akan ikut ter-refetch kalau tidak disebut eksplisit.
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['monthly', 'occupancy', 'price-adjustment'].includes(key as string))
                });
                toast.success('Restrictions saved successfully', { id: 'save-restrictions' });
                onSuccess(); // Panggil onSuccess untuk menutup modal dan membersihkan state di parent
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrorsMessage = (mutationError as any)?.response?.data?.message;

                    if (validationErrorsMessage === 'channex_validation_error') {
                        const validationErrors = (mutationError as any)?.response?.data?.errors;
                        setErrorMultiple(validationErrors.map((error: any) => error.warning));
                    } else {
                        const validationErrors = (mutationError as any)?.response?.data?.errors;
                        if (validationErrors) {
                            // Logic untuk menampilkan error validasi Zod
                        }
                    }
                    toast.error('Failed to save restrictions', { id: 'save-restrictions' });
                } else {
                    toast.error(mutationError?.message || 'Failed to save restrictions', { id: 'save-restrictions' });
                }
            }
        });
    }, [mutate, selection, rateId, restrictionType, queryClient, onSuccess, setError]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body>
                {isError && <FormError />}
                {isFetching && <FormSpinner />}
                {isSuccess && !isFetching && rateplan && (
                    <>
                        <div className="row mb-1 align-items-center">
                            <span className="col-sm-4 col-form-label d-flex justify-content-end">Room Type: </span>
                            <span className="col-sm-8 col-form-label">{rateplan.data.room_type?.name}</span>
                        </div>
                        <div className="row mb-1 align-items-center">
                            <span className="col-sm-4 col-form-label d-flex justify-content-end">Rate Plan: </span>
                            <span className="col-sm-8 col-form-label">{rateplan.data.name}</span>
                        </div>
                        <div className="row mb-3 align-items-center">
                            <span className="col-sm-4 col-form-label d-flex justify-content-end">Selected Dates: </span>
                            <span className="col-sm-8 col-form-label fw-bold">{selection.length} date(s) selected</span>
                        </div>

                        <div className='row mb-1 align-items-center'>
                            <label htmlFor='type' className='col-sm-4 col-form-label d-flex justify-content-end' style={{ fontSize: '14px' }}>Setup: </label>
                            <div className='col-sm-8'>
                                <Controller
                                    name='value.0.type'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={rateOptions}
                                            value={field.value ? rateOptions.find((option) => option.value === field.value) : null}
                                            onChange={(selected) => {
                                                field.onChange(selected?.value);
                                                onTypeChange(selected?.value ?? '');
                                            }}
                                            isDisabled={true} // Tipe tidak bisa diubah dalam mode bulk edit
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        {watch('value.0.type') === 'rate' && (
                            <div className='row mb-1 align-items-center'>
                                <label htmlFor='value' className='col-sm-4 col-form-label d-flex justify-content-end' style={{ fontSize: '14px' }}>Set Rate for All: </label>
                                <div className='col-sm-8'>
                                    <Controller
                                        name='value.0.rate' // Mengontrol input rate
                                        control={control}
                                        rules={{ required: "Rate value is required" }}
                                        render={({ field }) => (
                                            <CurrencyInput
                                                {...field}
                                                placeholder='Rp 0'
                                                className={clsx("app-form-control", errors?.value?.[0]?.rate && 'is-invalid')}
                                                onChange={(_value, rawValue) => field.onChange(rawValue)}
                                                value={field.value ?? 0}
                                            />
                                        )}
                                    />
                                    {errors?.value?.[0]?.rate && <div className='invalid-feedback d-block'>{(errors.value[0].rate as FieldError).message}</div>}
                                </div>
                            </div>
                        )}
                    </>
                )}
                {errorMultiple && errorMultiple.map((error: any, index: number) => (
                    <div key={index} className="alert alert-danger">
                        {Object.entries(error).map(([key, value]: any) => (
                            <div key={key}>
                                <strong>
                                    {key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}:
                                </strong> {value.join(', ')}
                            </div>
                        ))}
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isFetching || isPending}>
                    Apply Update
                </button>
            </Modal.Footer>
        </form>
    )
}

export default FormRateAvailability;